//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box } from '@mui/material'
import {
  LoanTransitionState,
  PoolRolloverState,
  PoolRolloverStateToString,
  PoolTypeEnum,
  RoleEnum,
} from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import {
  PoolAccountCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
} from '@shared/sharedTypes'
import React, { ReactElement, useEffect, useState } from 'react'

import LenderRequestWithdrawFlexForm from '@react/lender/LenderRequestWithdrawFlexForm'
import LenderTransferPoolTokens from '@react/lender/LenderTransferPoolTokens'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

import ButtonModal from '@react/components/ButtonModal'
import { poolDynamicStates } from '@react/fetchers/chainFetchers'
import { fetchPoolAccountAccessPairs, poolCrossChainTransferFetch } from '@react/fetchers/PoolFetchers'
import LenderDepositToPool from '@react/lender/LenderDepositToPool'
import LenderRequestEarlyWithdrawForm from '@react/lender/LenderRequestEarlyWithdrawForm'
import LenderRequestWithdrawForm from '@react/lender/LenderRequestWithdrawForm'
import LenderWithdrawForm from '@react/lender/LenderWithdrawForm'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import LenderTransferSource from '@react/transfer/LenderTransferSource'
import { logOT } from '@shared/loggingShared'
import LenderRequestWithdrawDynamicForm from './LenderRequestWithdrawDynamicForm'

import { otAssert } from '@shared/conversions'
import { PoolCrossChainTransferCTO, TransferStepNumbers } from '@shared/PoolCrossChainDataCTO'

export default function LenderPoolActionModals(props: { poolAddr: EthAddr; poolName: string }): ReactElement {
  const { poolAddr, poolName } = props

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { accountAddr, roleEnum },
    chainConfiguration: { providerReadOnly, frontendProviderUrl },
  } = useOtChainState()

  const chainTimestamp = useOtChainTimestamp()
  const [pooCrossChainTransferCTO, setPooCrossChainTransferCTO] = React.useState<PoolCrossChainTransferCTO | undefined>(
    undefined
  )

  const poolBaseOverviewCTO = useOtSelector((state) => {
    return state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  })
  const poolBaseAccountCTO = useOtSelector((state) => {
    return state.poolState.poolAccountCTOs?.[poolAddr ?? ETH_ADDR_ZERO]?.[accountAddr ?? ETH_ADDR_ZERO]
  })

  const [hasAccess, setHasAccess] = useState(false)
  const [depositStateActive, setDepositStateActive] = useState(true)
  const [withdrawStateActive, setWithdrawStateActive] = useState(true)

  useEffect(() => {
    async function loadParticipants() {
      if (poolAddr && accountAddr && frontendProviderUrl) {
        const poolAccountPairs = await fetchPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey, accountAddr)

        logOT.info('poolAccountPairs', poolAccountPairs)

        const isAllowed = poolAccountPairs.some((pair) => pair.poolAddr.toLowerCase() === poolAddr.toLowerCase())
        setHasAccess(isAllowed)
      } else {
        setHasAccess(false)
      }
    }
    loadParticipants()
  }, [poolAddr, accountAddr, frontendProviderUrl])

  useEffect(() => {
    if (serverApiBaseUrl && restAPIKey && poolAddr) {
      window.addEventListener(
        'modalClose',
        () => {
          logOT.debug('Fetch Data 1 modalClose event', poolAddr)
          poolCrossChainTransferFetch(serverApiBaseUrl, restAPIKey, poolAddr)
            .then((_pooCrossChainTransferCTO) => {
              logOT.debug('YYYYY Fetch _poolOffchainDataCTO', _pooCrossChainTransferCTO)
              setPooCrossChainTransferCTO(_pooCrossChainTransferCTO)
            })
            .catch((e) => {
              console.error('Fetch _poolOffchainDataCTO error', e)
            })
        },
        false
      )
    }

    return () => {
      window.removeEventListener(
        'modalClose',
        () => {
          logOT.debug('Fetch Data 2 modalClose event')
        },
        false
      )
    }
  }, [])
  useEffect(() => {
    if (poolAddr && serverApiBaseUrl && restAPIKey) {
      poolCrossChainTransferFetch(serverApiBaseUrl, restAPIKey, poolAddr).then((_pooCrossChainTransferCTO) => {
        setPooCrossChainTransferCTO(_pooCrossChainTransferCTO)
      })
    }
  }, [poolAddr])

  let poolOverviewCTO: PoolOverviewCTO | undefined = undefined
  let poolAccountCTO: PoolAccountCTO | undefined = undefined
  let poolFlexOverviewCTO: PoolFlexOverviewCTO | undefined = undefined
  let poolFlexAccountCTO: PoolFlexAccountCTO | undefined = undefined

  let poolDynamicOverviewCTO: PoolDynamicOverviewCTO | undefined = undefined
  let poolDynamicAccountCTO: PoolDynamicAccountCTO | undefined = undefined

  if (poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term) {
    poolOverviewCTO = poolBaseOverviewCTO as PoolOverviewCTO
    poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
  } else if (poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate) {
    poolFlexOverviewCTO = poolBaseOverviewCTO as PoolFlexOverviewCTO
    poolFlexAccountCTO = poolBaseAccountCTO as PoolFlexAccountCTO
  } else if (poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic) {
    poolDynamicOverviewCTO = poolBaseOverviewCTO as PoolDynamicOverviewCTO
    poolDynamicAccountCTO = poolBaseAccountCTO as PoolDynamicAccountCTO
  }

  logOT.info('poolBaseOverviewCTO?.poolType ', poolBaseOverviewCTO?.poolType)
  logOT.debug(
    'rolloverState',
    PoolRolloverStateToString[poolOverviewCTO?.rolloverState ?? 0],
    poolOverviewCTO?.rolloverTimeToActionWindow
  )

  if (!poolBaseOverviewCTO || !poolBaseAccountCTO) {
    logOT.debug('No poolBaseOverviewCTO or poolBaseAccountCTO')
    return <div></div>
  }

  if (roleEnum !== RoleEnum.LENDER) {
    logOT.info('No roleEnum')
    return <div></div>
  }

  const canInvestTerm: boolean =
    !!poolOverviewCTO &&
    poolOverviewCTO.rolloverState !== PoolRolloverState.CreateNextLoan &&
    poolOverviewCTO.rolloverState !== PoolRolloverState.EarlyRolloverFinalize &&
    poolOverviewCTO.rolloverState !== PoolRolloverState.RolloverFinalize &&
    poolOverviewCTO.rolloverState !== PoolRolloverState.LateRolloverFinalize &&
    chainTimestamp < poolOverviewCTO.requestedLoanState.depositClosingTimestamp
  const canInvestFlex: boolean = !!poolFlexOverviewCTO && !!poolFlexAccountCTO
  const canInvestDynamic: boolean = !!poolDynamicOverviewCTO && !!poolDynamicAccountCTO
  otAssert(providerReadOnly, 'providerReadOnly is not set')
  if (canInvestDynamic && poolDynamicOverviewCTO) {
    poolDynamicStates({
      poolControllerAddr: poolDynamicOverviewCTO.poolControllerAddr,
      providerReadOnly,
    }).then((state) => {
      setDepositStateActive(state.depositActive)
      setWithdrawStateActive(state.withdrawActive)
    })
  }

  logOT.debug('canInvestFlex modals poolBaseOverviewCTO', !!poolFlexOverviewCTO, !!poolFlexAccountCTO)

  const canRequestTerm: boolean =
    !!poolOverviewCTO &&
    !!poolAccountCTO &&
    Number(poolAccountCTO.maxWithdrawRequest) > 0 &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.EarlyRolloverFinalize &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.RolloverFinalize &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.LateRolloverFinalize &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.LateRepayWithdrawsAndFees &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.EarlyRepayWithdrawsAndFees &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.RepayWithdrawsAndFees

  const canRequestFlex: boolean =
    !!poolFlexOverviewCTO && !!poolFlexAccountCTO && Number(poolFlexAccountCTO.maxWithdrawRequest) > 0

  const canRequestDynamic: boolean =
    !!poolDynamicOverviewCTO && !!poolDynamicAccountCTO && Number(poolDynamicAccountCTO.maxWithdrawRequest) > 0

  const canWithdrawTerm: boolean = !!poolAccountCTO && Number(poolAccountCTO.maxRedeem) > 0

  const earlyRedeemRequestClosingTimestamp = poolOverviewCTO?.activeLoanState
    ? poolOverviewCTO.activeLoanState.accrualStartTimestamp +
      (poolOverviewCTO.activeLoanState.durationDays -
        poolOverviewCTO.activeLoanState.transferInWindowDurationDays -
        poolOverviewCTO.activeLoanState.transferOutWindowDurationDays) *
        24 *
        3600
    : Number.MAX_VALUE

  const canEarlyWithdrawTerm: boolean =
    !!poolOverviewCTO &&
    !!poolAccountCTO &&
    chainTimestamp < earlyRedeemRequestClosingTimestamp &&
    poolOverviewCTO?.activeLoanState.transitionState === LoanTransitionState.AccruingInterest &&
    Number(poolAccountCTO.maxWithdrawRequest) > 0 &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.EarlyRolloverFinalize &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.RolloverFinalize &&
    poolOverviewCTO?.rolloverState !== PoolRolloverState.LateRolloverFinalize

  logOT.debug('#Buttons canEarlyWithdrawTerm', canEarlyWithdrawTerm)
  logOT.debug(
    '#Buttons canEarlyWithdrawTerm',
    canEarlyWithdrawTerm,
    !!poolOverviewCTO,
    !!poolAccountCTO,
    chainTimestamp < earlyRedeemRequestClosingTimestamp,
    poolOverviewCTO?.activeLoanState.transitionState === LoanTransitionState.AccruingInterest,
    Number(poolAccountCTO?.maxWithdrawRequest) > 0,
    poolOverviewCTO?.rolloverState
  )

  logOT.info('action modals poolBaseOverviewCTO', !!poolBaseOverviewCTO)

  logOT.debug('pooCrossChainTransferCTO', pooCrossChainTransferCTO)

  const canTransferSource =
    pooCrossChainTransferCTO?.sourcePoolAddr === poolAddr &&
    pooCrossChainTransferCTO?.sourceWalletAddr === accountAddr &&
    pooCrossChainTransferCTO?.transferStep === TransferStepNumbers.Destination_Approved &&
    (poolFlexOverviewCTO ||
      (poolOverviewCTO &&
        (poolOverviewCTO.rolloverState === PoolRolloverState.EarlyRolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.RolloverInitiate ||
          poolOverviewCTO.rolloverState === PoolRolloverState.CreateNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.ApproveNextLoan ||
          poolOverviewCTO.rolloverState === PoolRolloverState.LateRolloverInitiate)))

  const canTransfer =
    (location.hostname === 'localhost' || location?.hostname?.endsWith('opentradedev.com')) && !canTransferSource

  return (
    <Box
      data-testid={'PoolActionModals'}
      px={0}
      py={2}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        direction: 'row',
        gap: 2,
      }}
    >
      {canTransferSource && (
        <ButtonModal title={'Approve Cross-Chain Transfer'} variant="tertiary">
          <LenderTransferSource poolName={poolName} poolAddr={poolAddr}></LenderTransferSource>
        </ButtonModal>
      )}
      {canRequestFlex && canTransfer && hasAccess && (
        <ButtonModal title={'Transfer Tokens'} variant="primary">
          <LenderTransferPoolTokens poolAddr={poolAddr}></LenderTransferPoolTokens>
        </ButtonModal>
      )}
      {canInvestTerm && hasAccess && (
        <ButtonModal title={'Invest'} variant="primary">
          <LenderDepositToPool poolAddr={poolAddr}></LenderDepositToPool>
        </ButtonModal>
      )}
      {canInvestFlex && hasAccess && (
        <ButtonModal title={'Invest'} variant="tertiary">
          <LenderDepositToPool poolAddr={poolAddr}></LenderDepositToPool>
        </ButtonModal>
      )}
      {canInvestDynamic && hasAccess && (
        <ButtonModal
          title={'Invest'}
          variant="tertiary"
          disabled={poolDynamicOverviewCTO?.state !== 1 || !depositStateActive}
        >
          <LenderDepositToPool poolAddr={poolAddr}></LenderDepositToPool>
        </ButtonModal>
      )}
      {canRequestDynamic && hasAccess && poolDynamicOverviewCTO && poolDynamicAccountCTO && (
        <ButtonModal
          title={'Withdraw Request'}
          variant="tertiary"
          disabled={poolDynamicOverviewCTO?.state !== 1 || !withdrawStateActive}
        >
          <LenderRequestWithdrawDynamicForm
            poolDynamicOverviewCTO={poolDynamicOverviewCTO}
            poolDynamicAccountCTO={poolDynamicAccountCTO}
          ></LenderRequestWithdrawDynamicForm>
        </ButtonModal>
      )}

      {canRequestFlex && !!poolFlexOverviewCTO && !!poolFlexAccountCTO && (
        <ButtonModal title={'Withdraw Request'} variant="primary" tooltip="Click here to request a withdraw">
          <LenderRequestWithdrawFlexForm
            poolFlexOverviewCTO={poolFlexOverviewCTO}
            poolFlexAccountCTO={poolFlexAccountCTO}
          />
        </ButtonModal>
      )}
      {canRequestTerm && !!poolOverviewCTO && !!poolAccountCTO && (
        <ButtonModal
          title={'Terminate Rollover'}
          variant="primary"
          tooltip="Click here to terminate automatic rollover"
        >
          <LenderRequestWithdrawForm poolOverviewCTO={poolOverviewCTO} poolAccountCTO={poolAccountCTO} />
        </ButtonModal>
      )}
      {canWithdrawTerm && !!poolOverviewCTO && !!poolAccountCTO && (
        <ButtonModal title={'Withdraw'} variant="primary">
          <LenderWithdrawForm poolOverviewCTO={poolOverviewCTO} poolAccountCTO={poolAccountCTO} />
        </ButtonModal>
      )}
      {canEarlyWithdrawTerm && !!poolOverviewCTO && !!poolAccountCTO && (
        <ButtonModal title={'Early Withdraw'} variant="tertiary">
          <LenderRequestEarlyWithdrawForm poolOverviewCTO={poolOverviewCTO} poolAccountCTO={poolAccountCTO} />
        </ButtonModal>
      )}
    </Box>
  )
}
